import { AlertTriangle, GitCompareArrows, GitMerge, Sparkles, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'

import type { VersionEnHistorial } from '../../api/tipos'
import { Boton, Modal } from '../../componentes/ui'
import { fraseDeConflicto } from './textos'

/**
 * **Nada se sobrescribe en silencio** (CLAUDE.md §18). Si la versión que se mira no parte de la
 * publicada —mientras se preparaba se publicó otra, casi siempre un arreglo del equipo—, en vez
 * de «Publicar» se enseña esto, con las cuatro salidas: ver qué cambia, juntarlas (si no tocan
 * lo mismo), volver a pedir el cambio sobre lo publicado (cuesta créditos y se dice antes) o
 * descartar la propia. El núcleo tampoco la publicaría: esto es decirlo antes de pulsar.
 */
export const CajaDeConflicto = ({
  publicada,
  ocupado,
  puedeRegenerar,
  alComparar,
  alJuntar,
  alRegenerar,
  alDescartar,
}: {
  publicada: null | Pick<VersionEnHistorial, 'comentario' | 'quien'> | undefined
  ocupado: boolean
  puedeRegenerar: boolean
  alComparar: () => void
  alJuntar: () => void
  alRegenerar: () => void
  alDescartar: () => void
}) => (
  <div className="flex flex-col gap-3 rounded-caja border border-aviso/40 bg-aviso-suave p-4 text-sm" role="status">
    <p className="flex items-start gap-2">
      <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0 text-aviso" />
      <span>{fraseDeConflicto(publicada)}</span>
    </p>
    <div className="flex flex-wrap gap-2">
      <Boton disabled={ocupado} icono={<GitCompareArrows />} onClick={alComparar} pequeno>
        Ver qué cambia
      </Boton>
      <Boton disabled={ocupado} icono={<GitMerge />} onClick={alJuntar} pequeno tipo="primario">
        Juntar las dos
      </Boton>
      {puedeRegenerar && (
        <Boton disabled={ocupado} icono={<Sparkles />} onClick={alRegenerar} pequeno>
          Pedirlo otra vez sobre lo publicado
        </Boton>
      )}
      <Boton disabled={ocupado} icono={<Trash2 />} onClick={alDescartar} pequeno tipo="peligro">
        Descartar el mío
      </Boton>
    </div>
    <p className="text-xs text-tinta-2">
      «Juntar» funciona si los dos cambios tocan partes distintas de la web. Pedirlo otra vez cuesta créditos: antes te decimos cuántos.
    </p>
  </div>
)

/** Una confirmación corta, con lo que va a pasar y cómo se deshace. */
export const Confirmacion = ({
  titulo,
  children,
  confirmar,
  peligro = false,
  ocupado,
  alConfirmar,
  cerrar,
}: {
  titulo: string
  children: ReactNode
  confirmar: string
  peligro?: boolean
  ocupado: boolean
  alConfirmar: () => void
  cerrar: () => void
}) => (
  <Modal
    ancho="max-w-md"
    cerrar={() => !ocupado && cerrar()}
    pie={
      <>
        <Boton disabled={ocupado} onClick={cerrar}>
          Cancelar
        </Boton>
        <Boton cargando={ocupado} onClick={alConfirmar} tipo={peligro ? 'peligro' : 'primario'}>
          {confirmar}
        </Boton>
      </>
    }
    titulo={titulo}
  >
    <div className="flex flex-col gap-3 text-sm text-tinta-2">{children}</div>
  </Modal>
)
